import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Tag, Pencil, Save } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AdminOffers = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ discount_type: 'none', discount_value: '0', discount_active: true, discount_start: '', discount_end: '' });

  useEffect(() => { fetchProducts(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/products`);
      setProducts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      discount_type: p.discount_type || 'none',
      discount_value: String(p.discount_value || 0),
      discount_active: p.discount_active !== false,
      discount_start: p.discount_start ? p.discount_start.slice(0, 10) : '',
      discount_end: p.discount_end ? p.discount_end.slice(0, 10) : ''
    });
  };

  const saveOffer = async (productId) => {
    try {
      const fd = new FormData();
      fd.append('discount_type', editForm.discount_type);
      fd.append('discount_value', editForm.discount_value);
      fd.append('discount_active', editForm.discount_active);
      fd.append('discount_start', editForm.discount_start);
      fd.append('discount_end', editForm.discount_end);
      await axios.put(`${BACKEND}/api/products/${productId}`, fd);
      setEditingId(null);
      fetchProducts();
    } catch (e) { alert('Error saving offer'); console.error(e); }
  };

  return (
    <div className="space-y-6" data-testid="admin-offers-page">
      <div>
        <h1 className="text-4xl font-light tracking-tighter text-white mb-2">Offers & Discounts</h1>
        <p className="text-[#A3A3A3]">Manage discounts for all products</p>
      </div>

      {loading ? <p className="text-[#A3A3A3]">Loading...</p> : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="offers-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Product</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Original Price</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Discount Type</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Discount Value</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Active</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Dates</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Final Price</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Action</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`offer-row-${p.id}`}>
                  <td className="py-4 px-4 text-white">{p.name}</td>
                  <td className="py-4 px-4 text-[#A3A3A3]">&#8377;{p.price.toLocaleString()}</td>
                  <td className="py-4 px-4">
                    {editingId === p.id ? (
                      <select value={editForm.discount_type} onChange={e => setEditForm({...editForm, discount_type: e.target.value})}
                        className="bg-[#0A0A0A] border border-white/20 text-white px-2 py-1 outline-none focus:border-[#D4AF37]" data-testid={`offer-type-select-${p.id}`}>
                        <option value="none">None</option>
                        <option value="percentage">Percentage</option>
                        <option value="flat">Flat</option>
                      </select>
                    ) : (
                      <span className="text-[#A3A3A3]">{p.discount_type || 'None'}</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    {editingId === p.id ? (
                      <input type="number" value={editForm.discount_value} onChange={e => setEditForm({...editForm, discount_value: e.target.value})} min="0"
                        className="w-24 bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-1 outline-none" data-testid={`offer-value-input-${p.id}`} />
                    ) : (
                      <span className="text-[#A3A3A3]">
                        {p.discount_type === 'percentage' ? `${p.discount_value}%` : p.discount_type === 'flat' ? `\u20b9${p.discount_value}` : '-'}
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    {editingId === p.id ? (
                      <input type="checkbox" checked={editForm.discount_active} onChange={e => setEditForm({...editForm, discount_active: e.target.checked})}
                        className="w-4 h-4 accent-[#D4AF37]" data-testid={`offer-active-${p.id}`} />
                    ) : (
                      <span className={p.discount_active !== false ? 'text-[#34C759]' : 'text-[#A3A3A3]'}>{p.discount_active !== false ? 'Yes' : 'No'}</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    {editingId === p.id ? (
                      <div className="space-y-2">
                        <input type="date" value={editForm.discount_start} onChange={e => setEditForm({...editForm, discount_start: e.target.value})}
                          className="bg-[#0A0A0A] border border-white/20 text-white px-2 py-1 outline-none focus:border-[#D4AF37]" data-testid={`offer-start-${p.id}`} />
                        <input type="date" value={editForm.discount_end} onChange={e => setEditForm({...editForm, discount_end: e.target.value})}
                          className="bg-[#0A0A0A] border border-white/20 text-white px-2 py-1 outline-none focus:border-[#D4AF37]" data-testid={`offer-end-${p.id}`} />
                      </div>
                    ) : (
                      <span className="text-[#A3A3A3] text-xs">
                        {p.discount_start ? p.discount_start.slice(0, 10) : 'Any'} - {p.discount_end ? p.discount_end.slice(0, 10) : 'Any'}
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-[#D4AF37] font-medium">&#8377;{p.final_price.toLocaleString()}</td>
                  <td className="py-4 px-4">
                    {editingId === p.id ? (
                      <button onClick={() => saveOffer(p.id)} className="text-[#34C759] hover:text-white" data-testid={`save-offer-${p.id}`}><Save size={16} /></button>
                    ) : (
                      <button onClick={() => startEdit(p)} className="text-[#A3A3A3] hover:text-white" data-testid={`edit-offer-${p.id}`}><Pencil size={16} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.length === 0 && <p className="text-center py-8 text-[#A3A3A3]">No products found</p>}
        </div>
      )}
    </div>
  );
};

export default AdminOffers;
